/**
 * BodyChart — reusable SVG body diagram with tap-to-drop markers.
 *
 * Two side-by-side outlines (front / back). Markers store relative
 * coordinates (0-100 percent) so the SVG can be resized without
 * re-anchoring. Read-only mode (``interactive={false}``) is used
 * when previewing an existing chart on the timeline.
 *
 * Marker kinds and intensity are picked in a compact toolbar above
 * the SVG. Clicking an existing marker in interactive mode selects
 * it so its note can be edited and it can be deleted.
 */
import { useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const KIND_COLORS = {
  pain:      { fill: "#dc2626", label: "Pain" },
  swelling:  { fill: "#0ea5e9", label: "Swelling" },
  numbness:  { fill: "#a855f7", label: "Numbness" },
  tingling:  { fill: "#f59e0b", label: "Tingling" },
  scar:      { fill: "#78716c", label: "Scar" },
  other:     { fill: "#059669", label: "Other" },
};

export default function BodyChart({
  markers = [],
  interactive = true,
  onChange,       // (newMarkers) => void
  activeKind = "pain",
  activeIntensity = 5,
}) {
  const [selectedId, setSelectedId] = useState(null);

  function place(side, evt) {
    if (!interactive) return;
    // Bounding rect gives absolute pixels; convert to 0-100 percent so
    // the marker sticks to the same anatomical spot at any resolution.
    const svg = evt.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = ((evt.clientX - rect.left) / rect.width) * 100;
    const y = ((evt.clientY - rect.top) / rect.height) * 100;
    const id = crypto.randomUUID();
    const marker = { id, side, x, y, kind: activeKind, intensity: activeIntensity, note: "" };
    onChange?.([...markers, marker]);
    setSelectedId(id);
  }

  function selectMarker(id, evt) {
    // Stop propagation so the click doesn't also drop a new marker.
    evt?.stopPropagation();
    setSelectedId((cur) => (cur === id ? null : id));
  }

  function removeMarker(id) {
    onChange?.(markers.filter((m) => m.id !== id));
    setSelectedId(null);
  }

  function updateSelectedNote(txt) {
    onChange?.(markers.map((m) => (m.id === selectedId ? { ...m, note: txt } : m)));
  }

  const selected = markers.find((m) => m.id === selectedId);

  return (
    <div className="space-y-3" data-testid="body-chart">
      <div className="grid grid-cols-2 gap-3">
        <BodySide side="front" markers={markers} interactive={interactive} onPlace={place} onSelect={selectMarker} selectedId={selectedId} />
        <BodySide side="back"  markers={markers} interactive={interactive} onPlace={place} onSelect={selectMarker} selectedId={selectedId} />
      </div>
      {interactive && selected && (
        <div className="rounded-md border border-zinc-200 bg-white p-3 shadow-sm" data-testid="body-chart-selected">
          <div className="flex items-center gap-2">
            <span
              className="h-3 w-3 rounded-full ring-2 ring-white shadow"
              style={{ backgroundColor: KIND_COLORS[selected.kind]?.fill }}
            />
            <span className="text-xs font-medium">
              {KIND_COLORS[selected.kind]?.label} · intensity {selected.intensity}/10 · {selected.side}
            </span>
            <button
              onClick={() => removeMarker(selected.id)}
              className="ml-auto text-red-600 hover:bg-red-50 p-1 rounded"
              data-testid="body-chart-remove-marker"
              title="Remove marker"
            ><X className="h-3.5 w-3.5" /></button>
          </div>
          <input
            type="text"
            value={selected.note || ""}
            onChange={(e) => updateSelectedNote(e.target.value)}
            placeholder="Note (optional)"
            className="mt-2 w-full h-8 px-2 text-sm border border-zinc-300 rounded"
            data-testid="body-chart-note-input"
          />
        </div>
      )}
    </div>
  );
}


// Legend + shared side renderer.

function BodySide({ side, markers, interactive, onPlace, onSelect, selectedId }) {
  const sideMarkers = markers.filter((m) => m.side === side);
  return (
    <div className="border border-zinc-200 rounded-md p-2 bg-zinc-50/40" data-testid={`body-chart-${side}`}>
      <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1 text-center">
        {side}
      </div>
      <svg
        viewBox="0 0 100 200"
        className={cn("w-full h-auto", interactive && "cursor-crosshair")}
        onClick={(e) => onPlace(side, e)}
        preserveAspectRatio="xMidYMid meet"
      >
        <BodyOutline side={side} />
        {sideMarkers.map((m) => (
          <g key={m.id} transform={`translate(${m.x} ${m.y})`}>
            <circle
              r={2.5}
              fill={KIND_COLORS[m.kind]?.fill || "#dc2626"}
              stroke="white"
              strokeWidth={0.5}
              opacity={m.id === selectedId ? 1 : 0.85}
              onClick={interactive ? (e) => onSelect(m.id, e) : undefined}
              style={interactive ? { cursor: "pointer" } : undefined}
            />
            <text y={0.9} textAnchor="middle" fontSize={2.5} fill="white" pointerEvents="none">{m.intensity}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}


/**
 * Very simple anatomical outline — one paths per view. Kept minimal so
 * the file stays under 200 lines; real anatomical detail would use an
 * SVG asset. Coordinates chosen to roughly resemble a human silhouette
 * at scale 100×200.
 */
function BodyOutline({ side }) {
  const stroke = "#71717a";
  const fill = "#fafafa";
  return (
    <g fill={fill} stroke={stroke} strokeWidth={0.4} strokeLinejoin="round">
      {/* Head */}
      <circle cx={50} cy={12} r={8} />
      {/* Neck */}
      <rect x={46} y={19} width={8} height={4} />
      {/* Torso */}
      <path d="M 34 24 L 66 24 L 68 40 L 66 80 L 60 96 L 40 96 L 34 80 L 32 40 Z" />
      {/* Arms */}
      <path d="M 34 24 L 22 30 L 20 55 L 22 78 L 26 78 L 28 55 L 32 32 Z" />
      <path d="M 66 24 L 78 30 L 80 55 L 78 78 L 74 78 L 72 55 L 68 32 Z" />
      {/* Legs */}
      <path d="M 40 96 L 38 130 L 40 165 L 44 180 L 48 180 L 48 130 L 46 96 Z" />
      <path d="M 60 96 L 62 130 L 60 165 L 56 180 L 52 180 L 52 130 L 54 96 Z" />
      {side === "front" && (
        <>
          {/* facial hint */}
          <circle cx={47} cy={11} r={0.6} fill={stroke} stroke="none" />
          <circle cx={53} cy={11} r={0.6} fill={stroke} stroke="none" />
        </>
      )}
    </g>
  );
}


export function BodyChartLegend() {
  return (
    <div className="flex flex-wrap gap-2 text-2xs">
      {Object.entries(KIND_COLORS).map(([k, v]) => (
        <div key={k} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: v.fill }} />
          <span className="text-zinc-600">{v.label}</span>
        </div>
      ))}
    </div>
  );
}
